import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

// Pure-copper pre-stranding (funie de cupru). Two constructions:
//  • STRANDED  (class-5 flexible): numberOfDrums bundles, each = numberOfWires fine
//    wires of wireDiameter, all twisted together. e.g. 4 × 18 × Ø0.4.
//  • MULTI-WIRE (solid concentric): numberOfWires=[1,6,12,…] wires per layer with
//    wireDiameter=[d0,d1,…] — 1 central + rings around it. e.g. 1+6, Ø1.7/2.14.

const COPPER = '#C57B43';
const COPPER_DARK = '#9A5A2C';

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const asArr = v => (Array.isArray(v) ? v.map(Number) : v == null || v === '' ? [] : [Number(v)]);
const first = v => (Array.isArray(v) ? Number(v[0]) : Number(v));

const PreStranding3D = ({ rope, height = 320 }) => {
  const scene = useMemo(() => {
    if (!rope) return null;
    const type = String(rope.type || '').toUpperCase();
    const overall = num(rope.diameter) || 4;
    const turns = 2.5;
    const lay = overall * 5;
    const h = turns * lay;
    const geoms = []; // { pts:[Vector3], r, color }

    // helix of a point orbiting the axis at `pitchR`, with a co-rotating local
    // offset (so a whole bundle twists as a rigid cluster)
    const helix = (pitchR, phase, ox0 = 0, oz0 = 0) => {
      const pts = [];
      const segs = 56;
      const total = turns * Math.PI * 2;
      for (let i = 0; i <= segs; i++) {
        const t = i / segs;
        const a = phase + t * total;
        const cx = Math.cos(a) * pitchR;
        const cz = Math.sin(a) * pitchR;
        const ox = ox0 * Math.cos(a) - oz0 * Math.sin(a);
        const oz = ox0 * Math.sin(a) + oz0 * Math.cos(a);
        pts.push(new THREE.Vector3(cx + ox, t * h - h / 2, cz + oz));
      }
      return pts;
    };
    const straight = r => {
      const pts = [];
      for (let s = 0; s <= 24; s++) pts.push(new THREE.Vector3(0, (s / 24) * h - h / 2, 0));
      return pts;
    };

    let label = '';

    if (type === 'MULTI-WIRE') {
      const counts = asArr(rope.numberOfWires);
      const dias = asArr(rope.wireDiameter);
      let outerR = 0;
      counts.forEach((cntRaw, k) => {
        const cnt = Math.max(1, Math.round(cntRaw));
        const d = dias[k] || dias[dias.length - 1] || 1;
        const wr = d / 2;
        let centerR;
        if (k === 0) { centerR = 0; outerR = wr; }
        else { centerR = outerR + wr; outerR = centerR + wr; }
        for (let i = 0; i < cnt; i++) {
          const color = k % 2 === 0 ? COPPER : COPPER_DARK;
          if (k === 0 && cnt === 1) geoms.push({ pts: straight(), r: wr, color });
          else geoms.push({ pts: helix(centerR, (i / cnt) * Math.PI * 2), r: wr, color });
        }
      });
      label = `${counts.map(c => Math.round(c)).join('+')} fire · Ø ${overall} mm${rope.section ? ` · ${rope.section} mm²` : ''}`;
    } else {
      // STRANDED (default)
      const drums = Math.max(1, Math.round(num(rope.numberOfDrums) || 1));
      const wpb = Math.max(1, Math.round(first(rope.numberOfWires) || 1));
      const wdia = first(rope.wireDiameter) || 0.4;
      const bundleR = (overall / 2) * 0.46;
      const macroR = Math.max(0, overall / 2 - bundleR);
      const rFine = Math.max(0.04, Math.min(wdia / 2, bundleR / (1 + Math.sqrt(wpb))));
      const total = drums * wpb;
      const step = total > 120 ? Math.ceil(total / 120) : 1; // perf cap on rendered wires
      let idx = 0;
      for (let b = 0; b < drums; b++) {
        const bphase = (b / drums) * Math.PI * 2;
        const color = b % 2 === 0 ? COPPER : COPPER_DARK;
        for (let w = 0; w < wpb; w++, idx++) {
          if (idx % step !== 0) continue;
          const ga = w * 2.399963; // golden angle → even sunflower packing
          const rr = bundleR * 0.82 * Math.sqrt(w / Math.max(1, wpb - 1));
          // place the bundle at angle bphase, offset the fine wire within it
          const bx = Math.cos(bphase) * macroR + Math.cos(ga) * rr;
          const bz = Math.sin(bphase) * macroR + Math.sin(ga) * rr;
          const off = Math.hypot(bx, bz);
          const offAng = Math.atan2(bz, bx);
          geoms.push({ pts: helix(off, offAng, 0, 0), r: rFine, color });
        }
      }
      label = `${drums} × ${wpb} fire Ø${wdia} · Ø ${overall} mm${rope.section ? ` · ${rope.section} mm²` : ''}`;
    }

    const tubes = geoms.map(g => {
      const curve = new THREE.CatmullRomCurve3(g.pts);
      return { geometry: new THREE.TubeGeometry(curve, Math.max(24, g.pts.length), g.r, 12, false), color: g.color };
    });
    // flat round faces at both ends → a real cross-section on each cap
    // (central + surrounding round wires for MULTI-WIRE; the bundles for STRANDED)
    const caps = [];
    geoms.forEach(g => {
      const a = g.pts[0];
      const b = g.pts[g.pts.length - 1];
      caps.push({ pos: [a.x, a.y, a.z], r: g.r, color: g.color, dir: -1 });
      caps.push({ pos: [b.x, b.y, b.z], r: g.r, color: g.color, dir: 1 });
    });
    const camDist = Math.max(overall * 2.6, h * 0.5);
    return { tubes, caps, camDist, label, h };
  }, [rope]);

  if (!scene || !scene.tubes.length) {
    return (
      <div style={{ height, background: '#0f1420', borderRadius: 6 }}
        className="d-flex align-items-center justify-content-center text-secondary small px-2 text-center">
        Fără date pentru vizualizarea funiei
      </div>
    );
  }

  const { tubes, caps, camDist, label, h } = scene;
  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [camDist, camDist * 0.45, camDist], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.85} />
        <directionalLight position={[camDist, h, camDist]} intensity={1.15} />
        <directionalLight position={[-camDist, -h * 0.3, -camDist]} intensity={0.4} />
        <OrbitControls enablePan enableZoom target={[0, 0, 0]} />
        {tubes.map((t, i) => (
          <mesh key={`t${i}`} geometry={t.geometry}>
            <meshStandardMaterial color={t.color} roughness={0.5} metalness={0.4} />
          </mesh>
        ))}
        {caps.map((c, i) => (
          <mesh key={`c${i}`} position={c.pos} rotation={[c.dir > 0 ? -Math.PI / 2 : Math.PI / 2, 0, 0]}>
            <circleGeometry args={[c.r, 24]} />
            <meshStandardMaterial color={c.color} roughness={0.35} metalness={0.55} side={2} />
          </mesh>
        ))}
        <Html position={[0, -h * 0.6, 0]} center>
          <div style={{ color: '#e0a878', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {label}
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default PreStranding3D;
